import type { GlassNavMode } from "@/components/public-nav-glass"
import type { ArticleChart, ArticleReceipt } from "@/lib/blog-article-bodies"

// Visual blocks an article section can carry next to its prose: a receipt for
// "here is one payment, line by line", and a two-part stacked bar for "here is
// what it adds up to". Both read the --pg-* glass tokens like the rest of the
// article, so they follow the page's light/dark toggle.

export function Receipt({ receipt }: { receipt: ArticleReceipt }) {
  return (
    <figure
      className="mt-8 max-w-md rounded-2xl px-6 py-5 font-mono text-[13px] leading-relaxed"
      style={{ background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)", color: "var(--pg-fg)" }}
    >
      <figcaption className="pb-3 text-[11px] uppercase tracking-[0.14em]" style={{ color: "var(--pg-muted)" }}>
        {receipt.title}
      </figcaption>
      <dl>
        {receipt.lines.map((line, i) =>
          line.divider ? (
            <div key={i} aria-hidden className="my-3 border-t border-dashed" style={{ borderColor: "var(--pg-line)" }} />
          ) : (
            <div key={i} className="flex items-baseline justify-between gap-6 py-0.5">
              <dt style={{ color: line.strong ? "var(--pg-fg)" : "var(--pg-muted)" }}>{line.label}</dt>
              <dd className={`tabular-nums ${line.strong ? "font-semibold" : ""}`}>{line.value}</dd>
            </div>
          ),
        )}
      </dl>
      {receipt.note && (
        <p className="mt-4 font-sans text-[12px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
          {receipt.note}
        </p>
      )}
    </figure>
  )
}

// Categorical slots 1 and 2 of the reference data-viz palette, stepped per
// mode and validated (CVD and normal-vision separation, 3:1 on the surface).
const SERIES: Record<GlassNavMode, [string, string]> = {
  light: ["#2a78d6", "#eb6834"],
  dark: ["#3987e5", "#d95926"],
}

export function StackedBars({ chart, mode }: { chart: ArticleChart; mode: GlassNavMode }) {
  const colors = SERIES[mode]
  const max = Math.max(...chart.rows.map((r) => r.values[0] + r.values[1]))
  const fmt = (n: number) => `${chart.unit ?? ""}${n.toLocaleString("en-US")}`

  return (
    <figure className="mt-8 max-w-2xl">
      <figcaption className="text-[14px] font-medium" style={{ color: "var(--pg-fg)" }}>
        {chart.title}
      </figcaption>
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]" style={{ color: "var(--pg-muted)" }}>
        {chart.series.map((name, i) => (
          <li key={name} className="inline-flex items-center gap-2">
            <span aria-hidden className="h-2.5 w-2.5 rounded-[3px]" style={{ background: colors[i] }} />
            {name}
          </li>
        ))}
      </ul>
      <div className="mt-5 space-y-4">
        {chart.rows.map((row) => {
          const total = row.values[0] + row.values[1]
          return (
            <div key={row.label}>
              <div className="flex items-baseline justify-between text-[13px]">
                <span style={{ color: "var(--pg-fg)" }}>{row.label}</span>
                <span className="tabular-nums font-medium" style={{ color: "var(--pg-fg)" }}>
                  {fmt(total)}
                </span>
              </div>
              <div
                className="mt-1.5 flex h-7 gap-[2px]"
                style={{ width: `${(total / max) * 100}%` }}
                role="img"
                aria-label={`${row.label}: ${chart.series[0]} ${fmt(row.values[0])}, ${chart.series[1]} ${fmt(row.values[1])}, total ${fmt(total)}`}
              >
                {row.values.map((v, i) => (
                  <div
                    key={i}
                    title={`${chart.series[i]}: ${fmt(v)}`}
                    className={`flex items-center px-2 text-[12px] font-medium text-white tabular-nums ${i === 0 ? "rounded-l-[4px]" : "rounded-r-[4px]"}`}
                    style={{ width: `${(v / total) * 100}%`, background: colors[i] }}
                  >
                    {fmt(v)}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
      {chart.note && (
        <p className="mt-4 text-[12px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
          {chart.note}
        </p>
      )}
    </figure>
  )
}
